import React from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { CheckboxSlider } from 'ui-components/components/checkbox-slider';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { FlexRow } from 'ui-components/components/flex/row';
import { NumberInput } from 'ui-components/components/number-input';
import { Text, TextType } from 'ui-components/components/text';

import { SimpleDropdown } from '../../../../components/simple-dropdown';
import { SimpleModal } from '../../../../components/simple-modal';
import { SellerSettingsOptions } from '../../../../enums/seller-settings/seller-settings-options';
import { useBinaryState } from '../../../../hooks/use-binary-state';

import { configurationActions } from './use-inventory-setup';
import { messages } from './messages';
import style from './style.module.less';

export const ConfigurationEditMode = ({
  onSave,
  onUpdate,
  onCancel,
  stopEditing,
  configuration,
  originalConfiguration,
  inventoryTrackItems,
  inventoryShipNoStockItems,
}) => {
  const onSetConfig = (type) => {
    return (value) => onUpdate({ type, value });
  };

  const [hasSeenConfirmation, setHasSeenConfirmation] = useBinaryState();
  const [isConfirmationModalOpen, openConfirmationModal, closeConfirmationModal] = useBinaryState();

  const toggleTracking = () => {
    const requiresConfirmation = originalConfiguration.inventoryTrackingEnabled && !hasSeenConfirmation;
    if (requiresConfirmation) {
      openConfirmationModal();
      return;
    }
    onUpdate({ type: configurationActions.toggleInventoryTrackingEnabled });
  };

  const disableInventoryTracking = () => {
    onUpdate({
      type: configurationActions.setInventoryTrackingEnabled,
      value: false,
    });
    setHasSeenConfirmation();
    closeConfirmationModal();
  };

  return (
    <>
      <SimpleModal
        actionMessage={messages.disableInventoryTrackingTooltipConfirmButton}
        headerMessage={messages.disableInventoryTrackingTooltipHeader}
        onAction={disableInventoryTracking}
        onCancel={closeConfirmationModal}
        onClose={closeConfirmationModal}
        show={isConfirmationModalOpen}
        size='medium'
      >
        <Text
          formattedMessageValues={{ b: (chunks) => <b>{chunks}</b> }}
          textType={TextType.Paragraph}
          value={messages.disableInventoryTrackingTooltipMessage}
        />
      </SimpleModal>
      <FlexRow className={style['section-header']}>
        <Text className={style['section-title']} textType={TextType.H3} value={messages.configuration} />
        <FlexRow className={style['section-panel']}>
          <Button
            className={style['section-panel-btn']}
            onClick={() => {
              onCancel();
              stopEditing();
            }}
            theme='tertiary'
          >
            <FormattedMessage {...messages.cancelButton} />
          </Button>
          <Button className={style['section-panel-btn']} onClick={() => onSave(stopEditing)} theme='primary'>
            <FormattedMessage {...messages.saveButton} />
          </Button>
        </FlexRow>
      </FlexRow>
      <FlexRow className={style['dropdown-option']}>
        <Text
          className={style['option-description']}
          formattedMessageValues={{
            b: (chunks) => <span className={style['underline-title']}>{chunks}</span>,
          }}
          textType={TextType.Paragraph}
          value={messages.noOnHandStockWarningOptionLabel}
        />
        <div className={style['dropdown-option-wrapper']}>
          <SimpleDropdown
            defaultTitle={<FormattedMessage {...SellerSettingsOptions.warnShip.messageDescriptor} />}
            dropdownMenuShouldHaveWidthOfButton
            items={inventoryShipNoStockItems}
            onItemSelected={onSetConfig(configurationActions.setInventoryShipNoStock)}
            selectedItemKey={configuration.inventoryShipNoStock}
          />
        </div>
      </FlexRow>
      <FlexRow className={style['dropdown-option']}>
        <Text
          className={style['option-description']}
          formattedMessageValues={{
            b: (chunks) => <span className={style['underline-title']}>{chunks}</span>,
          }}
          textType={TextType.Paragraph}
          value={messages.unknownOnHandStockWarningOptionLabel}
        />
        <div className={style['dropdown-option-wrapper']}>
          <SimpleDropdown
            defaultTitle={<FormattedMessage {...SellerSettingsOptions.warnShip.messageDescriptor} />}
            dropdownMenuShouldHaveWidthOfButton
            items={inventoryShipNoStockItems}
            onItemSelected={onSetConfig(configurationActions.setInventoryShipUnknown)}
            selectedItemKey={configuration.inventoryShipUnknown}
          />
        </div>
      </FlexRow>
      <FlexRow className={style['dropdown-option']}>
        <Text
          className={style['option-description']}
          formattedMessageValues={{
            b: (chunks) => <span className={style['underline-title']}>{chunks}</span>,
          }}
          textType={TextType.Paragraph}
          value={messages.locationNotTrackingInvWarningOptionLabel}
        />
        <DropdownMenu className={style['dropdown-option-wrapper']} dropdownMenuShouldHaveWidthOfButton variant='form'>
          <DropdownMenuToggler>{inventoryTrackItems[configuration.inventoryShipNoWarehouse]}</DropdownMenuToggler>
          <DropdownMenuItem name='warn' onClick={onSetConfig(configurationActions.setInventoryShipNoWarehouse)}>
            <FormattedMessage {...messages.warnOfInventoryAdjustmentOption} />
          </DropdownMenuItem>
          <DropdownMenuItem name='doNotWarn' onClick={onSetConfig(configurationActions.setInventoryShipNoWarehouse)}>
            <FormattedMessage {...messages.doNotWarnOfInventoryAdjustmentOption} />
          </DropdownMenuItem>
        </DropdownMenu>
      </FlexRow>
      <div className={style['settings-border']} />
      <FlexRow className={style['slider-option']}>
        <div>
          <Text className={style['allow-tracking-message']} textType={TextType.Paragraph} value={messages.allowInventoryTracking} />
          <span className={style['recommended-message']}>
            <FormattedMessage {...messages.recommended} />
          </span>
          <Text className={style['slider-caption']} textType={TextType.Caption} value={messages.enableInventoryTrackingDescription} />
        </div>
        <CheckboxSlider
          checkboxId='inventory-tracking-toggle'
          checked={configuration.inventoryTrackingEnabled}
          className={style.slider}
          onChange={toggleTracking}
        />
      </FlexRow>
      <div className={style['settings-border']} />
      <FlexRow className={style['threshold-option']}>
        <div>
          <Text textType={TextType.Paragraph} value={messages.defaultThresholdWarningOptionLabel} />
          <Text className={style['slider-caption']} textType={TextType.Caption} value={messages.defaultThresholdInfo} />
        </div>
        <FlexRow className={style['threshold-input-wrapper']}>
          <NumberInput
            className={style['threshold-input']}
            min={1}
            onChange={onSetConfig(configurationActions.setInventoryReOrderThreshold)}
            value={configuration.inventoryReOrderThreshold}
          />
          <Text className={style['threshold-label']} textType={TextType.Caption} value={messages.setThresholdWarningOption} />
        </FlexRow>
      </FlexRow>
    </>
  );
};
